import { useEffect, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { db } from '@/shared/firebase/config';
import {
  OFFLINE_THRESHOLD_MS,
  type ResidentStatus,
  type TrackedResident,
} from '@/shared/firebase/types';

export function useTrackedResidents(centerId: string | null) {
  const [residents, setResidents] = useState<TrackedResident[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!centerId) {
      setResidents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const r = ref(db, `centers/${centerId}/tracked`);
    return onValue(
      r,
      (snap) => {
        const v = snap.val() as Record<string, Omit<TrackedResident, 'uid'>> | null;
        const list: TrackedResident[] = v
          ? Object.entries(v).map(([uid, data]) => ({ uid, ...data }))
          : [];
        setResidents(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [centerId]);

  return { residents, loading };
}

export function residentStatus(r: TrackedResident, now = Date.now()): ResidentStatus {
  const hasActiveSos = r.alerts
    ? Object.values(r.alerts).some((a) => a.type === 'sos' && !a.acknowledged)
    : false;
  if (hasActiveSos) return 'sos';
  if (!r.latest) return 'offline';
  if (now - r.latest.ts > OFFLINE_THRESHOLD_MS) return 'offline';
  const gf = r.config?.geofence;
  if (gf) {
    const d = haversineMeters(gf.center, { lat: r.latest.lat, lng: r.latest.lng });
    return d > gf.radius ? 'outside' : 'inside';
  }
  return 'inside';
}

function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
